import React from 'react';
import { getUnreadReleaseCount } from './api';

/**
 * NOVEDADES SIN LEER: UN SOLO NÚMERO PARA TODA LA APLICACIÓN (20-oct-2026).
 *
 * Lo muestran dos lugares: el globo de *What's new* en el menú de la cuenta y el botón junto a *Refresh* en el
 * dashboard (*"junto a algun indicador si ya lo vieron"*).
 *
 * ## 🔴 Por qué no pide cada uno el suyo
 *
 * El menú de la cuenta vive en el sidebar, que **no se vuelve a montar al navegar**: pedía el número una vez, y su globo
 * se quedaba encendido después de leer las novedades, hasta recargar la página. Con un segundo indicador, uno se
 * apagaría y el otro no, y la pregunta *"¿ya lo vi?"* tendría dos respuestas.
 *
 * ## 🔑 Cómo funciona
 *
 * - Quien lo muestra usa `useUnreadReleaseCount`, que lo pide al servidor al montarse. Si dos lo piden a la vez (el
 *   dashboard y el menú, al entrar), comparten la petición.
 * - *What's new* lo actualiza al marcar como leído (`setUnreadReleaseCount`), y los dos indicadores se apagan juntos.
 * - Cuando ya nadie lo muestra (se cerró la sesión), se olvida: quien entre después no ve, ni un instante, el número
 *   del anterior. Una respuesta que llegue tarde, pedida antes de olvidarlo, se descarta.
 *
 * ⚠️ **Nunca lanza.** Es información de más, no una función: si el endpoint falla, el número se queda como estaba.
 */

let unread = 0;
/** Sube cada vez que el número se olvida. Una respuesta pedida con otra versión ya no aplica. */
let version = 0;
/** La petición en camino, con la versión con la que se pidió. Solo hay una por versión. */
let inflight: { version: number; promise: Promise<void> } | null = null;
const listeners = new Set<() => void>();

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      unread = 0;
      version += 1;
      inflight = null;
    }
  };
};

const getSnapshot = (): number => unread;

/** El número que dijo el servidor. Lo que no sea un entero positivo cuenta como 0. */
export const setUnreadReleaseCount = (next: number): void => {
  const value = Number.isFinite(next) && next > 0 ? Math.floor(next) : 0;
  if (value === unread) return;
  unread = value;
  listeners.forEach((listener) => listener());
};

/** Lo vuelve a pedir. Se puede llamar sin cuidado: no lanza y no repite una petición que ya va en camino. */
export const refreshUnreadReleaseCount = (): Promise<void> => {
  if (inflight && inflight.version === version) return inflight.promise;

  const asked = version;
  const promise = Promise.resolve()
    /** Dentro del `then` para que hasta un error síncrono termine en el `catch`. */
    .then(() => getUnreadReleaseCount())
    .then((count) => {
      if (asked === version) setUnreadReleaseCount(count);
    })
    .catch(() => {
      /** Silencio a propósito: ver arriba. */
    })
    .finally(() => {
      if (inflight?.version === asked) inflight = null;
    });

  inflight = { version: asked, promise };
  return promise;
};

/** Las novedades sin leer de quien tiene la sesión. Se pide al montar y se actualiza sola cuando cambia. */
export const useUnreadReleaseCount = (): number => {
  const count = React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  React.useEffect(() => {
    void refreshUnreadReleaseCount();
  }, []);

  return count;
};
